// STEP 4, `/analyze` — THE APP'S "ANALYZE" (WEB-PARITY-4: VD-1, VD-2, VD-5 to VD-9, VD-18).
//
// The app's screen (`AnalyzeView`, AppealAce/Features/Reasons/AnalyzeView.swift) laid out for a wide screen: the hero;
// then EITHER the one-voice no-case card with its two routes (`NoCaseVerdictCard`, D-12) OR the "Honest case check" card
// holding the VerdictCard — the verdict's icon and title, the strength · precision tile, the value number line, the
// strongest comps, the model's points, "What we recommend" and the caveats — "What the wider neighborhood says", and
// "Comps alone won't carry this" where the app offers it; and "Continue to value".
//
// THE CHECK RUNS ON ARRIVAL, as the app's does (`maybeAutoEvaluate`, :225-240), and the SERVER decides whether that is a
// new check or the one on file: a check over the same comp set answers again and spends nothing (contract 2.21.0). Every
// figure and fact is the server's — the verdict, the readout, the number line, the picks (`v2EvaluateCase`), and the
// neighbourhood's counts (`v2Value` NEW-14). Every word is the app's (`parity4Wording.ts`) or the server's.
//
// WEB-DESIGN-1 (R234): on the no-case result, "what your credit did" sits above "Save this and finish"
// (`CreditWork.tsx`) — the server's figures under the app's labels, its new captions drafts until Tyler approves them.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { EvaluateCaseResponse, SearchCompsResponse, ValueResponse } from "@contract";
import { useAppealId, useSession } from "../../app/session";
import { PaidRoute } from "../../components/PaidRoute";
import { Refusal } from "../../components/Refusal";
import type { V2Failure } from "../../transport/types";
import { CreditWork } from "./CreditWork";
import { StepHero } from "./parity3Parts";
import { Card4, Icon, Marked, NumberLine, ProgressLines4, Said4 } from "./parity4Parts";
import {
  analyzeFooter, dollars, hoodLines, meterHeadline, noCaseEvidence, noCaseMetrics, noCaseResultLine, noCaseVerdictLine,
  precisionLabel, recommendation, strongestLine, verdictIcon, verdictTint, verdictTitle,
} from "./parity4Rules";
import { say4, say4With, type Parity4WordingId } from "./parity4Wording";

const PROGRESS: readonly Parity4WordingId[] = ["analyze.progress.1", "analyze.progress.2", "analyze.progress.3", "analyze.progress.4"];

type R3 = EvaluateCaseResponse["decisions"]["R-3"];
type R3Value = NonNullable<Extract<R3, { ran: true }>["value"]>;

export function Analyze() {
  const session = useSession();
  const appealId = useAppealId();
  return (
    <PaidRoute<SearchCompsResponse>
      // The stored set, read: an empty `edit` changes nothing. The value and the check follow it, in the server's own
      // order (`compute` writes `head.value`, which the check requires).
      probe={() => session.transport.v2SearchComps.edit({ appealId })}
      deps={[session.transport, appealId, session.revision]}
    >
      {(comps) => <AnalyzeStep comps={comps} />}
    </PaidRoute>
  );
}

type CheckState =
  | { phase: "running" }
  | { phase: "done"; value: ValueResponse; check: EvaluateCaseResponse }
  | { phase: "noValue"; value: null; failure: V2Failure | null }
  | { phase: "failed"; failure: V2Failure };

function AnalyzeStep({ comps }: { comps: SearchCompsResponse }) {
  const session = useSession();
  const appealId = useAppealId();
  const navigate = useNavigate();
  const [state, setState] = useState<CheckState>({ phase: "running" });
  const [tick, setTick] = useState(0);
  const usable = comps.body.comps.some((c) => c.usable);

  useEffect(() => {
    let live = true;
    setState({ phase: "running" });
    void (async () => {
      // `canEvaluate` (:117-121): an assessed value and at least one usable comp. Without them the app never asks.
      if (!usable) {
        if (live) setState({ phase: "noValue", value: null, failure: null });
        return;
      }
      const value = await session.transport.v2Value.compute({ appealId });
      if (!live) return;
      if (!value.ok) {
        // A refusal states its reason, the server's (R112) — never a silent "no value".
        setState({ phase: "noValue", value: null, failure: value });
        return;
      }
      const check = await session.transport.v2EvaluateCase({ appealId, narrative: true });
      if (!live) return;
      setState(check.ok ? { phase: "done", value: value.response, check: check.response } : { phase: "failed", failure: check });
    })();
    return () => {
      live = false;
    };
  }, [session.transport, appealId, usable, tick]);

  const check = state.phase === "done" ? state.check : null;
  const r3 = check?.decisions["R-3"];
  const noCase: R3Value | null = r3 !== undefined && r3.ran && r3.value !== null ? r3.value : null;
  // The check has not answered yet: only "running" — "done", "noValue" and "failed" are each an answer.
  const checking = state.phase === "running";

  return (
    <div className="analyze-step" data-step="analyze">
      <StepHero icon="house" titleId="analyze.hero.title" bodyId="analyze.hero.body" />

      {noCase !== null ? (
        <>
          <NoCaseCard v={noCase} />
          {/* WEB-DESIGN-1: what the credit did, from the server's facts, above the honest ending (decision 5). */}
          {check !== null && <CreditWork v={noCase} comps={comps} check={check} />}
          <div className="step-footer centered nocase-routes" data-nocase-routes>
            {/* THE BUTTON AGREES WITH THE VERDICT (D-12): the honest ending is the primary; the escape hatch is plain text
                with its note. "Save this and finish" leaves for the owner's properties, as the app's leaves the flow. */}
            <button type="button" className="btn primary" data-held-copy="nocase.finish" onClick={() => navigate("/home")}>
              {say4("nocase.finish")}
            </button>
            <button type="button" className="link-route" onClick={() => navigate("/verdict")}>
              <Said4 id="nocase.continue" as="strong" />
              <Said4 id="nocase.continueNote" as="small" />
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="analyze-grid">
            <HonestCaseCheck
              state={state}
              usable={usable}
              onRun={() => setTick((t) => t + 1)}
            />
            <div className="analyze-side">
              {state.phase === "done" && <Neighborhood value={state.value} />}
              {check !== null && check.body.strengthen && <Strengthen onBack={() => navigate("/condition")} />}
            </div>
          </div>
          <div className="step-footer">
            {/* NOT USABLE UNTIL THE CHECK HAS ANSWERED (Tyler, launch day, 2026-10-14: "can the continue to value button be
                non functioning UNTIL it loads"): while it runs, the answer may be the no-case card, which replaces this
                button with the honest ending (D-12). Any answer opens it — a result, no value, or a failure — so a check
                that fails never traps the owner here (block vs degrade). The app's button is not held back
                (AnalyzeView.swift:208-213). */}
            <button
              type="button"
              className="btn primary"
              data-held-copy="analyze.continue"
              disabled={checking}
              aria-busy={checking}
              onClick={() => navigate("/verdict")}
            >
              {say4("analyze.continue")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── "Honest case check" (AnalyzeView.swift:251-338) ────────────────────────────────────────────────────────────────

function HonestCaseCheck({
  state, usable, onRun,
}: {
  state: CheckState;
  usable: boolean;
  onRun: () => void;
}) {
  const check = state.phase === "done" ? state.check : null;
  const narrative = check?.body.narrative ?? null;
  const footer = analyzeFooter(narrative?.verdict ?? null);
  return (
    <Card4 icon="sparkles" titleId="analyze.card.title" tint="primary" titleTint footer={<Said4 id={footer} />} data-honest-check={state.phase}>
      {narrative?.stale === true && (
        <p className="stale-note" role="note">
          <Icon name="warn" size={14} />
          <Said4 id="analyze.stale" />
        </p>
      )}
      {state.phase === "running" && <ProgressLines4 ids={PROGRESS} longRun="analyze.longRun" />}
      {check !== null && narrative !== null && state.phase === "done" && (
        <VerdictCard check={check} value={state.value} />
      )}
      {(state.phase === "failed" || state.phase === "noValue" || (check !== null && narrative === null)) && (
        <>
          <button type="button" className="run-check" disabled={!usable || state.phase === "noValue"} onClick={onRun}>
            <Icon name="sparkles" size={22} />
            <span>
              <Said4 id="analyze.run" as="strong" />
              <Said4 id="analyze.run.sub" as="small" />
            </span>
            <Icon name="chevron" size={20} />
          </button>
          {(!usable || state.phase === "noValue") && <Said4 id="analyze.needComps" as="p" className="muted small" />}
          {check !== null && narrative === null && check.body.narrativeReason === "evaluationCap" && (
            <Said4 id="analyze.limit" as="p" className="check-error" />
          )}
          {check !== null && narrative === null && check.body.narrativeReason !== "evaluationCap" && (
            <Said4 id="analyze.error" as="p" className="check-error" />
          )}
          {(state.phase === "failed" || (state.phase === "noValue" && state.failure !== null)) && (
            <Refusal failure={state.failure!} context={say4("analyze.card.title")} />
          )}
        </>
      )}
    </Card4>
  );
}

/** The VerdictCard (AnalyzeView.swift:656-905), every fact the server's. */
function VerdictCard({ check, value }: { check: EvaluateCaseResponse; value: ValueResponse }) {
  const narrative = check.body.narrative!;
  const quality = check.body.caseQuality;
  const meter = check.decisions["NEW-12"];
  const suggestion = value.body.value.suggestion;
  const line = check.body.numberLine;
  const tint = verdictTint(narrative.verdict);
  const rec = recommendation(narrative.verdict);
  const title = verdictTitle(narrative, quality?.precision ?? null, suggestion?.submarketHeterogeneous ?? false);
  return (
    // No "Source: v1.evaluateCase" under the points (WEB-PARITY-1B, VD-4); it stays on the element.
    <div className={`verdict-card t4-${tint}`} data-verdict={narrative.verdict ?? "unknown"} data-narrative-source={narrative.source}>
      <p className="verdict-title">
        <Icon name={verdictIcon(narrative.verdict)} size={18} />
        <span>{title}</span>
      </p>
      {quality !== null && (
        <div className={`quality-tile precision-${quality.precision}`} data-quality>
          <Icon name={quality.precision === "high" ? "seal" : quality.precision === "medium" ? "circle" : "warn"} size={14} />
          <div>
            <p className="quality-head" data-held-copy="verdict.quality">
              {meter.ran
                ? say4With("verdict.quality", { strength: meterHeadline(meter.value.label.text), precision: precisionLabel(quality.precision) })
                : precisionLabel(quality.precision)}
            </p>
            <p className="quality-blurb">{suggestion?.confidenceBlurb.text ?? quality.blurb.text}</p>
            {quality.caveat !== null && <p className="quality-caveat" data-copy-code={quality.caveat.code}>{quality.caveat.text}</p>}
          </div>
        </div>
      )}
      {line !== null && (line.gap !== null
        ? <NumberLine line={{ ...line, gap: line.gap }} />
        : <p className="implied-range" data-held-copy="verdict.compsImplyAbout">
            {say4With("verdict.compsImplyAbout", { low: dollars(line.low.amount), high: dollars(line.high.amount) })}
          </p>)}
      {narrative.strongest !== null && <p className="strongest-line" data-strongest>{strongestLine(narrative.strongest)}</p>}
      <ul className="verdict-points">
        {narrative.explanationPoints.map((point) => <li key={point}>{point}</li>)}
      </ul>
      {rec !== null && (
        <div className="recommend" data-recommendation>
          <Icon name="hand" size={14} />
          <div>
            <Said4 id="verdict.recommend" as="p" className="recommend-head" />
            <p>{rec}</p>
          </div>
        </div>
      )}
      {narrative.caveats.trim() !== "" && <p className="verdict-caveats">{narrative.caveats}</p>}
    </div>
  );
}

// ── "What the wider neighborhood says" (AnalyzeView.swift:505-535) ────────────────────────────────────────────────

function Neighborhood({ value }: { value: ValueResponse }) {
  const d = value.decisions["NEW-14"];
  if (!d.ran || d.value === null) return null;
  const lines = hoodLines(d.value);
  return (
    <Card4 icon={lines.caution ? "warn" : "bars"} titleId="hood.title" tint={lines.caution ? "accent" : "primary"} data-neighborhood={d.value.majority}>
      <p className="hood-counts" data-held-copy="hood.counts"><Marked text={lines.counts} /></p>
      <p className="muted small" data-held-copy="hood.basis">{lines.basis}</p>
      <p className={lines.caution ? "hood-majority caution" : "hood-majority"} data-held-copy={`hood.${d.value.majority}`}>{lines.majority}</p>
    </Card4>
  );
}

// ── "Comps alone won't carry this" (AnalyzeView.swift:540-568) ────────────────────────────────────────────────────

function Strengthen({ onBack }: { onBack: () => void }) {
  return (
    <Card4 icon="wrench" titleId="strengthen.title" tint="accent" data-strengthen>
      <Said4 id="strengthen.body" as="p" />
      <Said4 id="strengthen.addSale" as="p" className="muted small" />
      <button type="button" className="btn primary block" onClick={onBack} data-held-copy="strengthen.back">
        <Icon name="back" size={16} />
        {say4("strengthen.back")}
      </button>
      <Said4 id="strengthen.note" as="p" className="faint" />
    </Card4>
  );
}

// ── the one-voice no-case card (NoCaseVerdictCard.swift) ──────────────────────────────────────────────────────────

function NoCaseCard({ v }: { v: R3Value }) {
  const metrics = noCaseMetrics(v);
  return (
    <Card4 icon="shield" title={v.headline.text} tint="savings" data-no-case-shape={v.shape}>
      <p className="nocase-result" data-copy-code={v.headline.code}>{noCaseResultLine(v)}</p>
      <p className="nocase-verdict">{noCaseVerdictLine(v)}</p>
      <div className="nocase-metrics">
        {metrics.map((m, i) => (
          <div key={m.label} className={i === metrics.length - 1 ? "nocase-metric accent" : "nocase-metric"}>
            <b>{m.value}</b>
            <span>{m.label}</span>
          </div>
        ))}
      </div>
      <p className="muted">{noCaseEvidence(v)}</p>
      <div className="nocase-saves">
        <Said4 id="nocase.savesYou" as="h4" className="eyebrow savings" />
        <p data-copy-code={v.savesYou.code}>{v.savesYou.text}</p>
      </div>
      <div className="nocase-change">
        <Said4 id="nocase.wouldChange" as="h4" className="eyebrow muted-eyebrow" />
        <ul>
          {v.whatWouldChangeThis.map((line) => <li key={line.code} data-copy-code={line.code}>{line.text}</li>)}
        </ul>
      </div>
      <Said4 id="nocase.limitation" as="p" className="faint" />
    </Card4>
  );
}
